import { useEffect, useCallback, useRef, useState } from 'react';
import { getNotifications } from '@/services/notification.service';
import useNotificationsStore from '@/stores/notifications/notifications';

const PAGE_SIZE = 50;

const useNotificationsPolling = (interval = 30000, skip = false) => {
  const addNotifications = useNotificationsStore((state) => state.addNotifications);
  const updatesCount = useNotificationsStore((state) => state.notifications.updates.length);
  const totalCount = useNotificationsStore((state) => state.notifications.totalCount);
  const [loadingMore, setLoadingMore] = useState(false);
  const updatesCountRef = useRef(updatesCount);
  updatesCountRef.current = updatesCount;

  const fetchNotifications = useCallback(async () => {
    if (skip) return;

    try {
      const response = await getNotifications({ limit: PAGE_SIZE, offset: 0 });
      if (response.status === 200) {
        const data = response.data;
        addNotifications(data.results || data || [], data.total_count);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [addNotifications, skip]);

  const fetchMore = useCallback(async () => {
    if (skip || loadingMore) return;
    if (updatesCountRef.current >= totalCount) return;

    setLoadingMore(true);
    try {
      const response = await getNotifications({
        limit: PAGE_SIZE,
        offset: updatesCountRef.current,
      });
      if (response.status === 200) {
        const data = response.data;
        addNotifications(data.results || data || [], data.total_count);
      }
    } catch (error) {
      console.error('Failed to fetch more notifications:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [addNotifications, skip, loadingMore, totalCount]);

  useEffect(() => {
    if (skip) return;

    fetchNotifications();

    const pollingInterval = setInterval(fetchNotifications, interval);

    return () => clearInterval(pollingInterval);
  }, [fetchNotifications, interval, skip]);

  const hasMore = updatesCount < totalCount;

  return { fetchMore, loadingMore, hasMore };
};

export default useNotificationsPolling;
